import axios from 'axios'
import Cookies from 'js-cookie';
import config from './config';

const serverPath = config.apiUrl;

// Live fix 21 Oct 2026: expired logins go back to the login page and failed calls show a message, for every page.
export const ADMIN_COOKIE = 'loginSuccessfastenerwarehouseAdmin';
export const SESSION_EXPIRED_MSG = 'Your session expired. Please sign in again.';
export const SERVER_ERROR_MSG = "Couldn't reach the server. Please try again.";
export const ROLE_REFUSED_MSG = "Your login can't make this change. Please ask the account owner.";
const NOTICE_KEY = 'fwaAdminLoginNotice';

// Read the login cookie on every call (it used to be read once, when the app loaded).
export const getLoginData = () => {
    const raw = Cookies.get(ADMIN_COOKIE);
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
};

// When the token stops working (seconds since 1970), from the middle part of the JWT. null if it can't be read.
export const tokenExpiry = (token) => {
    try {
        const part = String(token || '').split('.')[1];
        if (!part) return null;
        const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
        return typeof json.exp === 'number' ? json.exp : null;
    } catch (e) {
        return null;
    }
};

// True when there is a login cookie and its token has not expired.
export const hasValidLogin = () => {
    const login = getLoginData();
    if (!login || !login.authToken) return false;
    const exp = tokenExpiry(login.authToken);
    return exp === null || exp * 1000 > Date.now();
};

// Save the login so the cookie expires with the token (1 day if the token's expiry can't be read).
export const saveLogin = (data) => {
    const exp = tokenExpiry(data && data.authToken);
    Cookies.set(ADMIN_COOKIE, JSON.stringify(data), {
        expires: exp ? new Date(exp * 1000) : 1,
        sameSite: 'strict',
        secure: window.location.protocol === 'https:',
    });
};

export const clearLogin = () => {
    Cookies.remove(ADMIN_COOKIE);
};

// The login page shows this message once (component/login.js).
export const takeLoginNotice = () => {
    try {
        const msg = sessionStorage.getItem(NOTICE_KEY);
        if (msg) sessionStorage.removeItem(NOTICE_KEY);
        return msg || '';
    } catch (e) {
        return '';
    }
};

let redirecting = false;
export const sendToLogin = (message = SESSION_EXPIRED_MSG) => {
    if (redirecting) return;
    redirecting = true;
    clearLogin();
    try {
        sessionStorage.setItem(NOTICE_KEY, message);
    } catch (e) {
        /* private window: the login page still opens, without the message */
    }
    window.location.href = `${config.baseUrl}`;
};

// A plain message at the top of any page. Works without a <Toaster /> on the page.
let bannerTimer = null;
export const showServerError = (message = SERVER_ERROR_MSG) => {
    if (typeof document === 'undefined' || !document.body) return;
    let el = document.getElementById('fwa-admin-error-banner');
    if (!el) {
        el = document.createElement('div');
        el.id = 'fwa-admin-error-banner';
        el.setAttribute('role', 'alert');
        el.style.cssText = 'position:fixed;top:12px;left:50%;transform:translateX(-50%);z-index:100000;background:#fff;color:#842029;border:1px solid #f5c2c7;border-left:4px solid #dc3545;border-radius:6px;padding:10px 16px;box-shadow:0 4px 14px rgba(0,0,0,.15);font-size:14px;max-width:90vw;';
        document.body.appendChild(el);
    }
    el.textContent = message;
    el.style.display = 'block';
    clearTimeout(bannerTimer);
    bannerTimer = setTimeout(() => { el.style.display = 'none'; }, 6000);
};

// Every admin call goes through here.
//  - 401/403: the login expired or was refused -> delete the cookie and go to the login page with a message.
//    Exception: 403 with code "role_forbidden" means this staff login may not make the change; show a message, stay.
//  - no answer, CORS block or 5xx -> "Couldn't reach the server. Please try again."
// The error is still passed on, so pages that catch it keep working. The login call is left to the login page.
const handleFailure = (path, error) => {
    const status = error && error.response ? error.response.status : 0;
    const code = error && error.response && error.response.data ? error.response.data.code : '';
    if (path !== 'adminLogin') {
        if (status === 403 && code === 'role_forbidden') {
            showServerError(ROLE_REFUSED_MSG);
        } else if (status === 401 || status === 403) {
            sendToLogin();
        } else if (!status || status >= 500) {
            showServerError();
        }
    }
    return Promise.reject(error);
};

export const request = (path, data, method) => {
    var options = {
        method: method,
        url: `${serverPath}/${path}`,
        headers: {
            'Content-Type': 'application/json'
        },
        dataType: 'json'
    };
    const loginData = getLoginData();
    if (loginData && loginData.authToken) {
        options.headers['Authorization'] = loginData.authToken
    }
    if (method === 'GET') {
        options['params'] = data
    } else {
        options['data'] = data
    }

    return axios(options).catch((error) => handleFailure(path, error))
}

export const requestFormData = (path, data, method) => {
    var form_data = new FormData();
    for (var key in data) {
        if (key === "bundles_file_url") {
            data[key].forEach((file) => {
                form_data.append('bundles_file_url', file);
            });
        } else {
            form_data.append(key, data[key]);
        }
    }
    const loginData = getLoginData();
    var options = {
        method: method,
        url: `${serverPath}/${path}`,
        data : form_data,
        headers: { authorization: loginData ? loginData.authToken : undefined },
    };
    return axios(options).catch((error) => handleFailure(path, error));
}

export const postRequest = async (path, data) => await request(path, data, 'POST')
export const getRequest = async (path, data) => await request(path, data, 'GET')
export const putRequest = async (path, data) => await request(path, data, 'PUT')
export const deleteRequest = async (path, data) => await request(path, data, 'DELETE')

export const postRequestFormData = async (path, data) => await requestFormData(path, data, 'POST')
